// User Management Controller (Frontend)
// File: controllers/userManagementController.js

class UserManagementController {
    constructor() {
      this.STORAGE_KEY = 'users_list';
      this.initializeDefaults();
    }
  
    initializeDefaults() {
      const existing = localStorage.getItem(this.STORAGE_KEY);
      if (!existing) {
        const defaults = [
          {
            id: '1',
            name: 'Super Admin',
            email: 'superadmin@tracklio.com',
            phone: '+91 9876543210',
            role: 'super_admin',
            department: 'Management',
            designation: 'System Administrator',
            status: 'active',
            avatar: '',
            joinedDate: '2024-01-01',
            lastLogin: new Date().toISOString(),
            permissions: ['all'],
            isEmailVerified: true,
            isTwoFactorEnabled: true
          },
          {
            id: '2',
            name: 'John Doe',
            email: 'john@tracklio.com',
            phone: '+91 9876543211',
            role: 'admin',
            department: 'IT',
            designation: 'IT Manager',
            status: 'active',
            avatar: '',
            joinedDate: '2024-01-15',
            lastLogin: new Date().toISOString(),
            permissions: ['dashboard', 'users', 'settings'],
            isEmailVerified: true,
            isTwoFactorEnabled: false
          },
          {
            id: '3',
            name: 'Jane Smith',
            email: 'jane@tracklio.com',
            phone: '+91 9876543212',
            role: 'manager',
            department: 'Sales',
            designation: 'Sales Manager',
            status: 'active',
            avatar: '',
            joinedDate: '2024-02-01',
            lastLogin: new Date().toISOString(),
            permissions: ['dashboard', 'reports', 'crm'],
            isEmailVerified: true,
            isTwoFactorEnabled: false
          },
          {
            id: '4',
            name: 'Bob Wilson',
            email: 'bob@tracklio.com',
            phone: '+91 9876543213',
            role: 'employee',
            department: 'Marketing',
            designation: 'Marketing Executive',
            status: 'active',
            avatar: '',
            joinedDate: '2024-03-01',
            lastLogin: new Date(Date.now() - 86400000).toISOString(),
            permissions: ['dashboard', 'profile'],
            isEmailVerified: true,
            isTwoFactorEnabled: false
          },
          {
            id: '5',
            name: 'Alice Brown',
            email: 'alice@tracklio.com',
            phone: '+91 9876543214',
            role: 'employee',
            department: 'HR',
            designation: 'HR Executive',
            status: 'inactive',
            avatar: '',
            joinedDate: '2024-02-15',
            lastLogin: new Date(Date.now() - 172800000).toISOString(),
            permissions: ['dashboard', 'hrms'],
            isEmailVerified: false,
            isTwoFactorEnabled: false
          }
        ];
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(defaults));
      }
    }
  
    getAllUsers() {
      try {
        const users = localStorage.getItem(this.STORAGE_KEY);
        return users ? JSON.parse(users) : [];
      } catch (error) {
        console.error('Error getting users:', error);
        return [];
      }
    }
  
    getUserById(userId) {
      try {
        const users = this.getAllUsers();
        return users.find(u => u.id === userId) || null;
      } catch (error) {
        console.error('Error getting user:', error);
        return null;
      }
    }
  
    createUser(userData) {
      try {
        const users = this.getAllUsers();
        const newUser = {
          id: Date.now().toString(),
          ...userData,
          joinedDate: new Date().toISOString().split('T')[0],
          lastLogin: null,
          isEmailVerified: false,
          isTwoFactorEnabled: false
        };
        users.push(newUser);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(users));
        return newUser;
      } catch (error) {
        console.error('Error creating user:', error);
        return null;
      }
    }
  
    updateUser(userId, updates) {
      try {
        const users = this.getAllUsers();
        const index = users.findIndex(u => u.id === userId);
        
        if (index !== -1) {
          users[index] = { ...users[index], ...updates };
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(users));
          return users[index];
        }
        return null;
      } catch (error) {
        console.error('Error updating user:', error);
        return null;
      }
    }
  
    deleteUser(userId) {
      try {
        const users = this.getAllUsers();
        const filtered = users.filter(u => u.id !== userId);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
        return true;
      } catch (error) {
        console.error('Error deleting user:', error);
        return false;
      }
    }
  
    toggleUserStatus(userId) {
      try {
        const users = this.getAllUsers();
        const user = users.find(u => u.id === userId);
        
        if (user) {
          user.status = user.status === 'active' ? 'inactive' : 'active';
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(users));
          return user;
        }
        return null;
      } catch (error) {
        console.error('Error toggling status:', error);
        return null;
      }
    }
  
    getUsersByRole(role) {
      try {
        const users = this.getAllUsers();
        return users.filter(u => u.role === role);
      } catch (error) {
        console.error('Error getting users by role:', error);
        return [];
      }
    }
  
    getUsersByDepartment(department) {
      try {
        const users = this.getAllUsers();
        return users.filter(u => u.department === department);
      } catch (error) {
        console.error('Error getting users by department:', error);
        return [];
      }
    }
  
    getStatistics() {
      try {
        const users = this.getAllUsers();
        return {
          total: users.length,
          active: users.filter(u => u.status === 'active').length,
          inactive: users.filter(u => u.status === 'inactive').length,
          verified: users.filter(u => u.isEmailVerified).length,
          twoFactorEnabled: users.filter(u => u.isTwoFactorEnabled).length,
          byRole: {
            super_admin: users.filter(u => u.role === 'super_admin').length,
            admin: users.filter(u => u.role === 'admin').length,
            manager: users.filter(u => u.role === 'manager').length,
            employee: users.filter(u => u.role === 'employee').length
          }
        };
      } catch (error) {
        console.error('Error getting statistics:', error);
        return { total: 0, active: 0, inactive: 0, verified: 0, twoFactorEnabled: 0, byRole: {} };
      }
    }
  
    searchUsers(query) {
      try {
        const users = this.getAllUsers();
        const lowerQuery = query.toLowerCase();
        return users.filter(u => 
          u.name.toLowerCase().includes(lowerQuery) ||
          u.email.toLowerCase().includes(lowerQuery) ||
          u.department.toLowerCase().includes(lowerQuery) ||
          u.designation.toLowerCase().includes(lowerQuery)
        );
      } catch (error) {
        console.error('Error searching users:', error);
        return [];
      }
    }
  
    exportUsers() {
      try {
        const users = this.getAllUsers();
        return JSON.stringify(users, null, 2);
      } catch (error) {
        console.error('Error exporting users:', error);
        return null;
      }
    }
  
    importUsers(jsonString) {
      try {
        const users = JSON.parse(jsonString);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(users));
        return true;
      } catch (error) {
        console.error('Error importing users:', error);
        return false;
      }
    }
  }
  
  export const userManagementController = new UserManagementController();